// Presentation helpers shared by the screen, the print layout and the PDF,
// so all three label and format the report identically for every period
// mode. They only pick and format values from the normalized ReportData —
// no arithmetic.

import { MonthStatus, ReconciliationStatus, ReportData, SummaryFigures } from "../types";
import { fmt } from "./format";

export const RECONCILIATION_LABEL: Record<ReconciliationStatus, string> = {
  not_entered: "Not Entered",
  reconciled: "Reconciled",
  needs_review: "Needs Review",
};

export const MONTH_STATUS_LABEL: Record<MonthStatus, string> = {
  open: "Open",
  reconciled: "Reconciled",
  closed: "Closed",
};

/** "Monthly Financial Report" for a month, "Financial Report" for any other period. */
export const reportTitle = (report: ReportData) =>
  report.period.mode === "month" ? "Monthly Financial Report" : "Financial Report";

export const reportFileName = (report: ReportData) => {
  const project = report.project.name.replace(/[^A-Za-z0-9]+/g, "_");
  const period =
    report.period.mode === "month"
      ? `${report.period.year}-${String(report.period.month).padStart(2, "0")}`
      : `${report.period.startDate || "inception"}_to_${report.period.endDate || "present"}`;
  return `${project}_${report.period.mode === "month" ? "Monthly_Report" : "Financial_Report"}_${period}.pdf`;
};

export const equationText = (report: Pick<SummaryFigures, "carryForward" | "incomeTotal" | "expenseTotal" | "balanceOnHand">) =>
  `${fmt(report.carryForward)} + ${fmt(report.incomeTotal)} - ${fmt(report.expenseTotal)} = ${fmt(report.balanceOnHand)}`;

/** Expense-style amounts held against a partner are shown in parentheses, e.g. ($291.71). */
const negativeParens = (n: number) => (n === 0 ? fmt(0) : `(${fmt(n)})`);

// ---------------------------------------------------------------------------
// Reconciliation — always present. Actual balances are entered per month, so
// a multi-month period shows its calculated balance and says so.
// ---------------------------------------------------------------------------

export type ReconciliationKey = ReconciliationStatus | "monthly_only";

export interface ReconciliationRows {
  calculated: string;
  actual: string;
  difference: string;
  status: string;
  statusKey: ReconciliationKey;
  note: string | null;
}

export const reconciliationRows = (report: ReportData): ReconciliationRows => {
  const r = report.monthly?.reconciliation;
  if (!r) {
    return {
      calculated: fmt(report.balanceOnHand),
      actual: "—",
      difference: "—",
      status: "Monthly only",
      statusKey: "monthly_only",
      note: "Actual balances are entered and reconciled per month. Select Monthly to reconcile.",
    };
  }
  return {
    calculated: fmt(r.calculated),
    actual: r.actual === null ? "Not entered" : fmt(r.actual),
    difference: r.difference === null ? "—" : fmt(r.difference),
    status: RECONCILIATION_LABEL[r.status],
    statusKey: r.status,
    note:
      r.status === "needs_review"
        ? "Monthly balance does not reconcile. Find and enter the missing transaction — no adjustment is created automatically."
        : r.status === "not_entered"
        ? "Enter the actual month-end bank + cash balance in Month Settings to reconcile."
        : null,
  };
};

// ---------------------------------------------------------------------------
// Partner Contribution / Mortgage Share — monthly only, and only for
// projects with two or more partners. When it applies but isn't set up, it
// is shown as "setup incomplete" with what's missing — never silently hidden.
// ---------------------------------------------------------------------------

export interface PartnerTable {
  headers: string[]; // ["Description", "Dinesh Kuna", "Ramesh Shan"]
  rows: { label: string; cells: string[]; emphasis?: boolean }[];
  payments: { name: string; amount: string }[]; // "Dinesh Kuna to Pay $2,578.36"
  mortgage: string; // "$6,447.00"
}

export type PartnerSection =
  | { state: "not_applicable" }
  | { state: "incomplete"; missing: string[] }
  | { state: "ready"; table: PartnerTable };

export const PARTNER_SETUP_INCOMPLETE = "Partner share setup incomplete";

export const partnerSection = (report: ReportData): PartnerSection => {
  const share = report.monthly?.partnerShare;
  if (!share || !share.applicable) return { state: "not_applicable" };
  if (!share.ready || share.mortgageAmount === null) {
    return { state: "incomplete", missing: share.missing.length ? share.missing : [share.message ?? "Settings incomplete"] };
  }
  const partners = share.partners;
  return {
    state: "ready",
    table: {
      headers: ["Description", ...partners.map((p) => p.name)],
      rows: [
        { label: "Existing Deposit Balance", cells: partners.map((p) => fmt(p.depositBalance)) },
        { label: "Cash on Hand", cells: partners.map((p) => negativeParens(p.cashOnHand)) },
        { label: "Adjusted Balance", cells: partners.map((p) => fmt(p.adjustedBalance)) },
        { label: "Mortgage Contribution", cells: partners.map((p) => fmt(p.contribution ?? 0)) },
        { label: "Final Contribution Balance", cells: partners.map((p) => fmt(p.finalBalance ?? 0)), emphasis: true },
      ],
      payments: partners.map((p) => ({ name: p.name, amount: fmt(p.contribution ?? 0) })),
      mortgage: fmt(share.mortgageAmount),
    },
  };
};

/** Back-compat helper: the ready table, or null. */
export const partnerTable = (report: ReportData): PartnerTable | null => {
  const section = partnerSection(report);
  return section.state === "ready" ? section.table : null;
};
